import { compileExpression } from './expression/index.js';
import { compileMatchTest } from './matchTests.js';
import { at, conditionShape, INSTANCE_EXPRESSION_SCOPE, invalidAt, isUnsafeKey, matchForm, readOwn, safeFunctionSchema, unrecognized, valueShape, } from './schema.js';
import { hasNoValue } from './values.js';
export { compareFinite, hasNoValue, isAbsent, isEmpty, isTrue, looseEquals, } from './values.js';
const DEFAULT_DEFINITION = 'safe function definition';
export function invalid(message, fragment) {
    return invalidAt({ path: '', definition: DEFAULT_DEFINITION }, message, fragment);
}
export const readOwnName = (name, _site, field) => field === undefined
    ? (subject) => readOwn(subject, name)
    : (subject) => readOwn(readOwn(subject, name), field);
export const safeFunctionCompilers = {
    condition: (fragment, site) => compileConditionAt(fragment, site),
    matchTest: (fragment, site) => compileMatchTest(fragment, site),
    value: (fragment, site) => compileValueAt(fragment, site),
};
export function createSite(options = {}, schema = safeFunctionSchema, compilers = safeFunctionCompilers) {
    return {
        path: options.path ?? '',
        definition: options.definition ?? DEFAULT_DEFINITION,
        schema,
        compilers,
        readName: options.readName ?? readOwnName,
        expression: options.expression ?? INSTANCE_EXPRESSION_SCOPE,
        call: options.call,
    };
}
export function readNameAt(name, site, field) {
    return (site.readName ?? readOwnName)(name, site, field);
}
export function compileExpressionAt(source, site, scope = site.expression ?? INSTANCE_EXPRESSION_SCOPE) {
    try {
        return compileExpression(source, {
            params: scope.params,
            implicitScope: scope.implicitScope,
            receivers: scope.receivers,
            unavailableReceivers: scope.unavailableReceivers,
        });
    }
    catch (error) {
        return invalidAt(site, error.message);
    }
}
export function compileConditionAt(condition, site) {
    if (typeof condition === 'function') {
        return condition;
    }
    if (!condition || typeof condition !== 'object' || Array.isArray(condition)) {
        return unrecognized(conditionShape, condition, site);
    }
    const entries = Object.entries(condition).map(([name, test]) => {
        const entrySite = at(site, name);
        if (isUnsafeKey(name)) {
            invalidAt(entrySite, `the name "${name}" is not allowed`, condition);
        }
        return {
            read: readNameAt(name, entrySite),
            test: compileMatchTest(test, entrySite),
        };
    });
    return (subject, context) => entries.every(({ read, test }) => test(read(subject, context), subject, context));
}
export function compileCondition(condition, options = {}) {
    return compileConditionAt(condition, createSite(options));
}
export function parseTemplate(template, site = {
    path: '',
    definition: DEFAULT_DEFINITION,
}) {
    if (typeof template !== 'string') {
        invalidAt(site, 'template must be a string', template);
    }
    const segments = [];
    let literal = '';
    for (let i = 0; i < template.length; i++) {
        const char = template[i];
        if (char === '\\' && (template[i + 1] === '{' || template[i + 1] === '}')) {
            literal += template[i + 1];
            i++;
            continue;
        }
        if (char !== '{') {
            literal += char;
            continue;
        }
        const end = template.indexOf('}', i + 1);
        if (end === -1) {
            invalidAt(site, 'template has an unclosed "{"', template);
        }
        const placeholder = template.slice(i + 1, end).trim();
        if (!placeholder) {
            invalidAt(site, 'template has an empty "{}" placeholder', template);
        }
        const [name, field, ...rest] = placeholder.split('.');
        if (!name || field === '' || rest.length) {
            invalidAt(site, `template placeholder "{${placeholder}}" must be a name, or a name and one field`, template);
        }
        if (literal) {
            segments.push({ literal });
            literal = '';
        }
        segments.push(field === undefined ? { name } : { name, field });
        i = end;
    }
    if (literal) {
        segments.push({ literal });
    }
    return segments;
}
export function compileTemplate(template, site = createSite()) {
    const parts = parseTemplate(template, site).map((segment) => 'literal' in segment
        ? () => segment.literal
        : readNameAt(segment.name, site, segment.field));
    return (subject, context) => parts
        .map((part) => {
        const value = part(subject, context);
        return hasNoValue(value) ? '' : String(value);
    })
        .join('');
}
export function compileValueAt(value, site) {
    if (typeof value === 'function') {
        return value;
    }
    if (typeof value === 'string') {
        return readNameAt(value, site);
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
        return unrecognized(valueShape, value, site);
    }
    const [form] = matchForm(valueShape, value, site);
    const fields = value;
    if (form === 'template') {
        return compileTemplate(fields.template, at(site, 'template'));
    }
    const evaluate = compileExpressionAt(fields.expression, at(site, 'expression'));
    return (subject, context) => evaluate(subject, context);
}
export function compileValue(value, options = {}) {
    return compileValueAt(value, createSite(options));
}
